import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth is carried entirely by the backend's httpOnly `session_token` cookie (Emergent Auth).
// The token is never stored in JS-accessible storage, so an XSS payload can't read it.
axios.defaults.withCredentials = true;

// On an expired/invalid session, send the user back to login.
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = String(err.config?.url || "");
    const path = window.location.pathname;
    const onAuthCallback = path.includes("/auth/callback") || window.location.hash?.includes("session_id=");
    // Never hijack the OAuth callback: a stray 401 (e.g. field-config) must not
    // redirect mid session-exchange. Also skip auth endpoints themselves.
    const skipRedirect = url.includes("/auth/") || url.includes("/field-config") || onAuthCallback;
    if (err.response?.status === 401 && !skipRedirect) {
      // Flag it so the login screen can explain the bounce (survives the full-page redirect).
      try { sessionStorage.setItem("sd:auth_expired", "1"); } catch { /* ignore */ }
      if (path !== "/login") window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const api = {
  // ---- auth (Emergent-managed Google login) ----
  createSession: (sessionId) => axios.post(`${API}/auth/session`, {}, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data),
  me: () => axios.get(`${API}/auth/me`).then((r) => r.data),
  logout: () => axios.post(`${API}/auth/logout`).then((r) => r.data),
  acceptLegal: () => axios.post(`${API}/legal/accept`).then((r) => r.data),
  deleteAccount: () => axios.delete(`${API}/me`).then((r) => r.data),
  updateMyName: (name) => axios.patch(`${API}/me`, { name }).then((r) => r.data),
  exportMyData: () => axios.get(`${API}/me/export.csv`, { responseType: "blob" }).then((r) => r.data),
  exportFullData: () => axios.get(`${API}/export/full`, { responseType: "blob" }).then((r) => r.data),
  getIngestToken: () => axios.get(`${API}/ingest/token`).then((r) => r.data),
  rotateIngestToken: () => axios.post(`${API}/ingest/token/rotate`).then((r) => r.data),
  getImportEvents: () => axios.get(`${API}/import-events`).then((r) => r.data),
  dismissImportEvents: (opts) => axios.post(`${API}/import-events/seen`, opts || {}).then((r) => r.data),
  clearTourReview: (id) => axios.post(`${API}/guests/${id}/clear-review`).then((r) => r.data),

  // ---- admin: user access management ----
  adminListUsers: () => axios.get(`${API}/admin/users`).then((r) => r.data),
  adminPendingCount: () => axios.get(`${API}/admin/pending-count`).then((r) => r.data),
  adminApproveUser: (id) => axios.post(`${API}/admin/users/${id}/approve`).then((r) => r.data),
  adminDenyUser: (id) => axios.post(`${API}/admin/users/${id}/deny`).then((r) => r.data),
  adminAiUsage: () => axios.get(`${API}/admin/ai-usage`).then((r) => r.data),

  extract: (image_base64) => axios.post(`${API}/extract`, { image_base64 }).then((r) => r.data),
  listGuests: () => axios.get(`${API}/guests`).then((r) => r.data),
  getGuest: (id) => axios.get(`${API}/guests/${id}`).then((r) => r.data),
  createGuest: (fields, profile = {}) => axios.post(`${API}/guests`, { fields, profile }).then((r) => r.data),
  updateGuest: (id, fields, profile = {}) => axios.put(`${API}/guests/${id}`, { fields, profile }).then((r) => r.data),
  setDayLogged: (id, date, logged) => axios.post(`${API}/guests/${id}/day-logged`, { date, logged }).then((r) => r.data),
  deleteGuest: (id) => axios.delete(`${API}/guests/${id}`).then((r) => r.data),
  restoreGuest: (id) => axios.post(`${API}/guests/${id}/restore`).then((r) => r.data),
  listTrash: () => axios.get(`${API}/trash`).then((r) => r.data),
  purgeTrashItem: (id) => axios.delete(`${API}/trash/${id}`).then((r) => r.data),
  emptyTrash: () => axios.delete(`${API}/trash`).then((r) => r.data),
  stats: () => axios.get(`${API}/stats`).then((r) => r.data),
  needsUpdateCount: () => axios.get(`${API}/stats/needs-update`).then((r) => r.data),
  listProfiles: () => axios.get(`${API}/profiles`).then((r) => r.data),
  getProfile: (name) => axios.get(`${API}/profiles/${encodeURIComponent(name)}`).then((r) => r.data),
  updateProfile: (name, data) => axios.put(`${API}/profiles/${encodeURIComponent(name)}`, data).then((r) => r.data),
  upcomingCelebrations: () => axios.get(`${API}/celebrations/upcoming`).then((r) => r.data),
  celebrationsMap: () => axios.get(`${API}/celebrations/map`).then((r) => r.data),
  getFieldConfig: () => axios.get(`${API}/field-config`).then((r) => r.data),
  saveFieldConfig: (cfg) => axios.put(`${API}/field-config`, cfg).then((r) => r.data),
  renameProfile: (name, new_name) => axios.post(`${API}/profiles/${encodeURIComponent(name)}/rename`, { new_name }).then((r) => r.data),
  duplicateProfiles: (name) => axios.get(`${API}/profiles/duplicates`, { params: name ? { name } : {} }).then((r) => r.data),
  profilePhoto: (name, params) => axios.get(`${API}/profiles/${encodeURIComponent(name)}/photo`, { params }).then((r) => r.data),
  deleteProfilePhoto: (name, params) => axios.delete(`${API}/profiles/${encodeURIComponent(name)}/photo`, { params }).then((r) => r.data),
  tourDuplicates: () => axios.get(`${API}/tours/duplicates`).then((r) => r.data),
  mergeTours: (keep, merge) => axios.post(`${API}/tours/merge`, { keep, merge }).then((r) => r.data),
  mergeProfiles: (primary, names) => axios.post(`${API}/profiles/merge`, { primary, names }).then((r) => r.data),
  extractSchedule: (image_base64) => axios.post(`${API}/extract-schedule`, { image_base64 }).then((r) => r.data),
  listShifts: () => axios.get(`${API}/shifts`).then((r) => r.data),
  createShifts: (shifts) => axios.post(`${API}/shifts`, { shifts }).then((r) => r.data),
  applySchedule: (shifts, picks, delete_ids) => axios.post(`${API}/schedule/apply`, { shifts, picks, delete_ids }).then((r) => r.data),
  previewSchedule: (shifts) => axios.post(`${API}/schedule/apply`, { shifts, preview: true }).then((r) => r.data),
  undoSchedule: () => axios.post(`${API}/schedule/undo`).then((r) => r.data),
  deleteShift: (id) => axios.delete(`${API}/shifts/${id}`).then((r) => r.data),
  listCallouts: () => axios.get(`${API}/callouts`).then((r) => r.data),
  createCallout: (body) => axios.post(`${API}/callouts`, body).then((r) => r.data),
  updateCallout: (id, body) => axios.put(`${API}/callouts/${id}`, body).then((r) => r.data),
  deleteCallout: (id) => axios.delete(`${API}/callouts/${id}`).then((r) => r.data),
  calloutRemoveSource: (id, body) => axios.post(`${API}/callouts/${id}/remove-source`, body).then((r) => r.data),
  revertCallout: (id) => axios.post(`${API}/callouts/${id}/revert`).then((r) => r.data),
  getCalloutBanks: () => axios.get(`${API}/callout-banks`).then((r) => r.data),
  saveCalloutBanks: (body) => axios.put(`${API}/callout-banks`, body).then((r) => r.data),
  bulkCreateCallouts: (items) => axios.post(`${API}/callouts/bulk`, { items }).then((r) => r.data),
  extractCallouts: (text) => axios.post(`${API}/callouts/extract-text`, { text }).then((r) => r.data),
  undoCalloutImport: (batch_id) => axios.post(`${API}/callouts/import/undo`, { batch_id }).then((r) => r.data),
  dropTourDay: (id, date) => axios.post(`${API}/guests/${id}/drop-day`, { date }).then((r) => r.data),
  updateShift: (id, patch) => axios.patch(`${API}/shifts/${id}`, patch).then((r) => r.data),
  promoteShift: (id) => axios.post(`${API}/shifts/${id}/promote`).then((r) => r.data),
  clearAll: () => axios.delete(`${API}/admin/clear-all`).then((r) => r.data),
  bulkCreate: (items) => axios.post(`${API}/guests/bulk`, { items }).then((r) => r.data),
  importExtractFile: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/import/extract`, fd).then((r) => r.data);
  },
  importExtractText: (text) => axios.post(`${API}/import/extract-text`, { text }).then((r) => r.data),
  undoImport: (batch_id) => axios.post(`${API}/guests/import/undo`, { batch_id }).then((r) => r.data),
  notionStatus: () => axios.get(`${API}/notion/status`).then((r) => r.data),
  notionConnect: (body) => axios.post(`${API}/notion/connect`, body).then((r) => r.data),
  notionCreateDatabase: () => axios.post(`${API}/notion/create-database`).then((r) => r.data),
  notionSettings: (body) => axios.post(`${API}/notion/settings`, body).then((r) => r.data),
  notionSync: (full = true) => axios.post(`${API}/notion/sync?full=${full}`).then((r) => r.data),
  notionDisconnect: () => axios.post(`${API}/notion/disconnect`).then((r) => r.data),
  guestNames: () => axios.get(`${API}/guest-names`).then((r) => r.data),
  getViewings: () => axios.get(`${API}/viewings`).then((r) => r.data),
  addViewing: (value) => axios.post(`${API}/viewings`, { value }).then((r) => r.data),
  getParkHours: () => axios.get(`${API}/park-hours`).then((r) => r.data),
  askInsights: (question, history = []) => axios.post(`${API}/insights/ask`, { question, history }).then((r) => r.data),

  // ---- app lock (passcode + biometric) ----
  appLockStatus: () => axios.get(`${API}/app-lock/status`).then((r) => r.data),
  setAppPin: (pin) => axios.post(`${API}/app-lock/pin`, { pin }).then((r) => r.data),
  unlockAppPin: (pin) => axios.post(`${API}/app-lock/pin/unlock`, { pin }).then((r) => r.data),
  disableAppLock: () => axios.post(`${API}/app-lock/disable`).then((r) => r.data),
  setRelock: (minutes) => axios.post(`${API}/app-lock/relock`, { minutes }).then((r) => r.data),
  resetAppLock: () => axios.post(`${API}/app-lock/reset`).then((r) => r.data),
  bioRegOptions: () => axios.post(`${API}/app-lock/biometric/register/options`).then((r) => r.data),
  bioRegVerify: (credential) => axios.post(`${API}/app-lock/biometric/register/verify`, { credential }).then((r) => r.data),
  bioAuthOptions: () => axios.post(`${API}/app-lock/biometric/auth/options`).then((r) => r.data),
  bioAuthVerify: (credential) => axios.post(`${API}/app-lock/biometric/auth/verify`, { credential }).then((r) => r.data),
  removeBiometric: () => axios.delete(`${API}/app-lock/biometric`).then((r) => r.data),

  pushVapidKey: () => axios.get(`${API}/push/vapid-key`).then((r) => r.data),
  pushSubscribe: (sub) => axios.post(`${API}/push/subscribe`, sub).then((r) => r.data),
  pushUnsubscribe: (sub) => axios.post(`${API}/push/unsubscribe`, sub).then((r) => r.data),
  pushTest: () => axios.post(`${API}/push/test`).then((r) => r.data),
  pushPrefs: () => axios.get(`${API}/push/prefs`).then((r) => r.data),
  pushSetPrefs: (prefs) => axios.put(`${API}/push/prefs`, prefs).then((r) => r.data),
};
